/**
 * IANA time-zone math on top of Intl.DateTimeFormat — no date library.
 * Used for kid quiet hours and schedule recurrence. All instants are epoch ms.
 */

export type ZonedParts = {
  year: number;
  month: number; // 1–12
  day: number; // 1–31
  hour: number; // 0–23
  minute: number;
  second: number;
  weekday: number; // 0 = Sunday … 6 = Saturday
};

export type Every = {
  kind: 'daily' | 'weekdays' | 'weekly';
  weekday?: number;
  at: string; // 'HH:MM'
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      weekday: 'short',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    formatter(timeZone);
    return true;
  } catch {
    return false;
  }
}

export function zonedParts(ms: number, timeZone: string): ZonedParts {
  const parts: Record<string, string> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(ms))) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: WEEKDAYS.indexOf(parts.weekday ?? 'Sun'),
  };
}

/** Offset of `timeZone` from UTC at instant `ms`, in ms (positive east of UTC). */
export function tzOffsetMs(ms: number, timeZone: string): number {
  const p = zonedParts(ms, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/**
 * Wall-clock (y, m, d, h, min) in `timeZone` → epoch ms. Day values may overflow
 * (d = 32 means next month), matching Date.UTC semantics. Non-existent local
 * times (DST gap) resolve to the instant after the gap.
 */
export function zonedToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): number {
  const wall = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  let guess = wall - tzOffsetMs(wall, timeZone);
  for (let i = 0; i < 3; i++) {
    const next = wall - tzOffsetMs(guess, timeZone);
    if (next === guess) break;
    guess = next;
  }
  return guess;
}

export function parseHHMM(at: string): { hour: number; minute: number } | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(at);
  if (!m) return null;
  return { hour: Number(m[1]), minute: Number(m[2]) };
}

export function validateEvery(every: Every): string | null {
  if (!parseHHMM(every.at)) return '"at" must be HH:MM (24h)';
  if (every.kind === 'weekly') {
    if (every.weekday === undefined || !Number.isInteger(every.weekday) || every.weekday < 0 || every.weekday > 6) {
      return '"weekday" (0–6, 0 = Sunday) is required for weekly schedules';
    }
  } else if (every.weekday !== undefined) {
    return '"weekday" is only valid for weekly schedules';
  }
  return null;
}

/**
 * The first instant strictly after `fromMs` at which `every` fires, in `timeZone`.
 * Walks day by day from the zoned date of `fromMs` (at most 8 days).
 */
export function nextOccurrence(every: Every, fromMs: number, timeZone: string): number {
  const hm = parseHHMM(every.at);
  if (!hm) throw new Error(`invalid time ${every.at}`);
  const today = zonedParts(fromMs, timeZone);
  for (let offset = 0; offset <= 8; offset++) {
    const dayUtc = new Date(Date.UTC(today.year, today.month - 1, today.day + offset));
    const weekday = dayUtc.getUTCDay();
    const matches =
      every.kind === 'daily' ||
      (every.kind === 'weekdays' && weekday >= 1 && weekday <= 5) ||
      (every.kind === 'weekly' && weekday === every.weekday);
    if (!matches) continue;
    const candidate = zonedToUtc(
      dayUtc.getUTCFullYear(),
      dayUtc.getUTCMonth() + 1,
      dayUtc.getUTCDate(),
      hm.hour,
      hm.minute,
      timeZone,
    );
    if (candidate > fromMs) return candidate;
  }
  throw new Error('no occurrence found within 8 days');
}

export const QUIET_START_HOUR = 21;
export const QUIET_END_HOUR = 7;

/** Kid quiet hours: 21:00–07:00 in the circle's time zone. */
export function isQuietHours(ms: number, timeZone: string): boolean {
  const { hour } = zonedParts(ms, timeZone);
  return hour >= QUIET_START_HOUR || hour < QUIET_END_HOUR;
}

/** The next 07:00 in `timeZone` at or after `ms` (only meaningful when inside quiet hours). */
export function quietHoursEnd(ms: number, timeZone: string): number {
  const p = zonedParts(ms, timeZone);
  const dayOffset = p.hour >= QUIET_START_HOUR ? 1 : 0;
  return zonedToUtc(p.year, p.month, p.day + dayOffset, QUIET_END_HOUR, 0, timeZone);
}

/** When a push for this member may go out: now, or deferred to 07:00 for kids in quiet hours. */
export function pushDeliveryTime(nowMs: number, timeZone: string, kid: boolean): number {
  if (kid && isQuietHours(nowMs, timeZone)) return quietHoursEnd(nowMs, timeZone);
  return nowMs;
}
